import { useState, useRef } from "react";
import { DollarSign, PieChart, Printer, Loader2, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { setPageTitle, setCanonical, generateSpeakableSchema } from "@/utils/seo";
import { formatMoney } from "@/utils/taxCalculator";
import FinancialDisclaimer from "@/components/FinancialDisclaimer";
import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BudgetPlannerPage() {
  const [form, setForm] = useState({
    monthly_income: "", rent: "", utilities: "", groceries: "",
    transportation: "", insurance: "", debt_payments: "", savings_goal: "", other_expenses: ""
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    setPageTitle("Free Budget Planner - Personalized Financial Plan");
    setCanonical("/budget-planner");
  }, []);

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    if (!form.monthly_income || !form.rent) return;
    setLoading(true);
    try {
      const payload = {};
      Object.keys(form).forEach(k => { payload[k] = parseFloat(form[k]) || 0; });
      const res = await axios.post(`${API}/budget-planner`, payload);
      setResult(res.data);
    } catch { setResult(null); }
    setLoading(false);
  };

  const handlePrint = () => { window.print(); };

  const fields = [
    { key: "monthly_income", label: "Monthly Income (After Tax)", placeholder: "5,000", required: true },
    { key: "rent", label: "Rent / Mortgage", placeholder: "1,500", required: true },
    { key: "utilities", label: "Utilities (Electric, Water, Internet)", placeholder: "200" },
    { key: "groceries", label: "Groceries & Food", placeholder: "400" },
    { key: "transportation", label: "Transportation (Gas, Transit)", placeholder: "150" },
    { key: "insurance", label: "Insurance (Health, Car, etc.)", placeholder: "300" },
    { key: "debt_payments", label: "Debt Payments (Loans, Cards)", placeholder: "200" },
    { key: "other_expenses", label: "Other Expenses", placeholder: "200" },
    { key: "savings_goal", label: "Monthly Savings Goal", placeholder: "500" },
  ];

  const healthColor = result?.budget_health === "Healthy" ? "text-emerald-700 bg-emerald-50" : result?.budget_health === "Tight" ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
  const HealthIcon = result?.budget_health === "Healthy" ? CheckCircle : result?.budget_health === "Tight" ? AlertTriangle : AlertTriangle;

  return (
    <div data-testid="budget-planner-page">
      <script type="application/ld+json">{JSON.stringify(generateSpeakableSchema("/budget-planner", ["Free Budget Planner"], ["Enter your income and expenses to get a personalized budget plan with AI-powered advice and a printable report."]))}</script>
      <section className="hero-pattern py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <PieChart className="w-7 h-7 text-emerald-700" />
            </div>
            <h1 className="speakable-headline text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3">
              Free Budget Planner
            </h1>
            <p className="speakable-summary text-base text-slate-500 max-w-lg mx-auto">
              Enter your income and expenses. Get a personalized budget plan with AI-powered advice and a printable report.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <Card className="shadow-lg" data-testid="budget-form">
              <CardContent className="p-6">
                <h2 className="text-sm font-semibold text-slate-900 mb-4">Your Monthly Numbers</h2>
                <div className="space-y-3">
                  {fields.map(f => (
                    <div key={f.key}>
                      <label className="text-xs text-slate-500 mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                        <input type="number" value={form[f.key]} onChange={e => update(f.key, e.target.value)}
                          placeholder={f.placeholder} data-testid={`budget-${f.key}`}
                          className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 outline-none font-mono" />
                      </div>
                    </div>
                  ))}
                </div>
                <Button onClick={handleSubmit} disabled={loading || !form.monthly_income || !form.rent}
                  className="w-full mt-4 bg-emerald-700 hover:bg-emerald-800 shadow-lg shadow-emerald-700/20"
                  data-testid="budget-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PieChart className="w-4 h-4 mr-2" />}
                  Generate My Budget Plan
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <div ref={printRef}>
              {result ? (
                <div className="space-y-4 fade-in-up" data-testid="budget-result">
                  {/* Health Score */}
                  <Card className="shadow-lg">
                    <CardContent className="p-6 text-center">
                      <HealthIcon className={`w-10 h-10 mx-auto mb-2 ${result.budget_health === "Healthy" ? "text-emerald-600" : result.budget_health === "Tight" ? "text-amber-600" : "text-red-600"}`} />
                      <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold ${healthColor}`} data-testid="budget-health">{result.budget_health}</span>
                      <div className="grid grid-cols-3 gap-3 mt-4">
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-[10px] text-slate-500">Income</p>
                          <p className="text-sm font-bold font-mono text-slate-800">{formatMoney(result.monthly_income)}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-[10px] text-slate-500">Expenses</p>
                          <p className="text-sm font-bold font-mono text-slate-800">{formatMoney(result.total_expenses)}</p>
                        </div>
                        <div className={`rounded-lg p-2 ${result.remaining >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                          <p className="text-[10px] text-slate-500">Remaining</p>
                          <p className={`text-sm font-bold font-mono ${result.remaining >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatMoney(result.remaining)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Expense Breakdown */}
                  <Card>
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Expense Breakdown</h3>
                      {Object.entries(result.expenses_breakdown).filter(([k, v]) => v > 0 && k !== 'savings_goal').map(([key, val]) => (
                        <div key={key} className="mb-2">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-mono text-slate-700">{formatMoney(val)}</span>
                          </div>
                          <Progress value={Math.min((val / result.monthly_income) * 100, 100)} className="h-1.5" />
                        </div>
                      ))}
                      <div className="mt-3 pt-3 border-t text-xs text-slate-500">
                        <p>Rent is <strong className="text-slate-700">{result.rent_ratio}%</strong> of income {result.rent_ratio > 30 ? '(above recommended 30%)' : '(within recommended 30%)'}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 50/30/20 Targets */}
                  <Card>
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">50/30/20 Rule Targets</h3>
                      {[
                        { label: "Needs (50%)", target: result.needs_target, color: "bg-emerald-500" },
                        { label: "Wants (30%)", target: result.wants_target, color: "bg-blue-500" },
                        { label: "Savings (20%)", target: result.savings_target, color: "bg-amber-500" },
                      ].map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b border-slate-50 last:border-0">
                          <span className="text-xs text-slate-600">{r.label}</span>
                          <span className="text-xs font-mono font-semibold text-slate-800">{formatMoney(r.target)}/mo</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* AI Advice */}
                  {result.ai_advice && (
                    <Card className="border-emerald-200 bg-emerald-50/50">
                      <CardContent className="p-5">
                        <h3 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> Personalized Tips
                        </h3>
                        <p className="text-xs text-emerald-700 leading-relaxed whitespace-pre-line">{result.ai_advice}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Print Button */}
                  <Button onClick={handlePrint} variant="outline" className="w-full" data-testid="budget-print">
                    <Printer className="w-4 h-4 mr-2" /> Print My Budget Plan
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center min-h-[200px]">
                    <div className="text-center text-slate-400">
                      <PieChart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm">Enter your numbers to generate your personalized budget plan</p>
                    </div>
                  </div>
                  {/* Google AdSense while waiting */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5 text-center">
                    <p className="text-[10px] text-slate-400 mb-2">Advertisement</p>
                    <div className="bg-slate-50 rounded-lg py-16 border border-dashed border-slate-200">
                      <p className="text-xs text-slate-400">Google AdSense</p>
                      <p className="text-[10px] text-slate-300">300x250</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <FinancialDisclaimer />

          {/* Apartment Affiliate Banner */}
          <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline block">
            <div className="p-4 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 border border-blue-200 rounded-xl mt-4 flex items-center gap-4 hover:shadow-md transition-all" data-testid="budget-sponsor">
              <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop" alt="Furnished apartment" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Sponsored</p>
                <p className="text-sm font-semibold text-slate-900">Planning a Move? Get $250 Off Your First Furnished Apartment</p>
                <p className="text-xs text-slate-500">Move freely, stay comfortably. Furnished apartments in 200+ cities. *Limited time offer. Terms apply.</p>
              </div>
              <span className="text-xs font-bold text-blue-600 whitespace-nowrap hidden md:inline">Claim $250 Off &rarr;</span>
            </div>
          </a>
        </div>
      </section>
    </div>
  );
}
